import { useState } from 'react';
import { Download, FileText, Table, Image, Calendar, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface ExportConfig {
  format: 'csv' | 'xlsx' | 'pdf' | 'json';
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  includeImages: boolean;
  filters: Record<string, any>;
  fields: string[];
}

interface ExportOption {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  dataSource: string;
  availableFields: { key: string; label: string; type: 'text' | 'number' | 'date' | 'boolean' }[];
  supportedFormats: string[];
}

const exportOptions: ExportOption[] = [
  {
    id: 'guards',
    title: 'Guards Data',
    description: 'Export guard profiles and information',
    icon: FileText,
    dataSource: 'profiles',
    availableFields: [
      { key: 'full_name', label: 'Full Name', type: 'text' },
      { key: 'email', label: 'Email', type: 'text' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'created_at', label: 'Created Date', type: 'date' },
      { key: 'Role', label: 'Role', type: 'text' }
    ],
    supportedFormats: ['csv', 'xlsx', 'pdf', 'json']
  },
  {
    id: 'teams',
    title: 'Teams Data',
    description: 'Export team information and member counts',
    icon: Table,
    dataSource: 'teams',
    availableFields: [
      { key: 'name', label: 'Team Name', type: 'text' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'created_at', label: 'Created Date', type: 'date' },
      { key: 'member_count', label: 'Member Count', type: 'number' }
    ],
    supportedFormats: ['csv', 'xlsx', 'pdf', 'json']
  },
  {
    id: 'schedules',
    title: 'Schedules Data',
    description: 'Export schedule and shift information',
    icon: Calendar,
    dataSource: 'team_schedules',
    availableFields: [
      { key: 'start_date', label: 'Start Date', type: 'date' },
      { key: 'end_date', label: 'End Date', type: 'date' },
      { key: 'location', label: 'Location', type: 'text' },
      { key: 'assigned_guards', label: 'Assigned Guards', type: 'text' },
      { key: 'team_id', label: 'Team ID', type: 'text' }
    ],
    supportedFormats: ['csv', 'xlsx', 'pdf', 'json']
  },
  {
    id: 'reports',
    title: 'Reports Data',
    description: 'Export reports and submissions',
    icon: FileText,
    dataSource: 'reports',
    availableFields: [
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'content', label: 'Content', type: 'text' },
      { key: 'status', label: 'Status', type: 'text' },
      { key: 'submitted_at', label: 'Submitted Date', type: 'date' },
      { key: 'team_id', label: 'Team ID', type: 'text' }
    ],
    supportedFormats: ['csv', 'xlsx', 'pdf', 'json']
  }
];

interface AdvancedExportProps {
  onExport?: (config: ExportConfig & { dataType: string }) => void;
}

export const AdvancedExport: React.FC<AdvancedExportProps> = ({ onExport }) => {
  const [selectedOption, setSelectedOption] = useState<ExportOption | null>(null);
  const [config, setConfig] = useState<ExportConfig>({
    format: 'csv',
    dateRange: { start: null, end: null },
    includeImages: false,
    filters: {},
    fields: []
  });
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const handleExport = async () => {
    if (!selectedOption) return;

    setIsExporting(true);
    try {
      // Build query based on configuration
      let query: any;
      switch (selectedOption.dataSource) {
        case 'profiles':
          query = supabase.from('profiles').select('*');
          break;
        case 'teams':
          query = supabase.from('teams').select('*');
          break;
        case 'team_schedules':
          query = supabase.from('team_schedules').select('*');
          break;
        case 'reports':
          query = supabase.from('reports').select('*');
          break;
        default:
          query = supabase.from('profiles').select('*');
      }

      // Apply date range filter
      if (config.dateRange.start && config.dateRange.end) {
        query = query
          .gte('created_at', config.dateRange.start.toISOString())
          .lte('created_at', config.dateRange.end.toISOString());
      }

      // Apply additional filters
      Object.entries(config.filters).forEach(([key, value]) => {
        if (value) {
          query = query.eq(key, value);
        }
      });

      const { data, error } = await query;

      if (error) throw error;

      // Process data based on selected fields
      const processedData = data?.map(item => {
        if (config.fields.length > 0) {
          // Only include selected fields
          const filtered: Record<string, any> = {};
          config.fields.forEach(field => {
            if (item[field] !== undefined) {
              filtered[field] = item[field];
            }
          });
          return filtered;
        }
        return item;
      });

      // Generate export based on format
      await generateExport(processedData || [], config.format, selectedOption.title);

      toast({
        title: "Export Successful",
        description: `${selectedOption.title} has been exported as ${config.format.toUpperCase()}`
      });

      onExport?.({ ...config, dataType: selectedOption.id });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  const generateExport = async (data: any[], exportFormat: string, title: string) => {
    const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
    const filename = `${title.toLowerCase().replace(/\s+/g, '_')}_${timestamp}`;

    switch (exportFormat) {
      case 'csv':
        downloadCSV(data, filename);
        break;
      case 'json':
        downloadJSON(data, filename);
        break;
      case 'xlsx':
        // Would require xlsx library in real implementation
        toast({
          title: "Coming Soon",
          description: "XLSX export will be available soon"
        });
        break;
      case 'pdf':
        // Would require pdf generation library in real implementation
        toast({
          title: "Coming Soon",
          description: "PDF export will be available soon"
        });
        break;
      default:
        downloadCSV(data, filename);
    }
  };

  const downloadCSV = (data: any[], filename: string) => {
    if (data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || '';
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    link.click();
  };

  const downloadJSON = (data: any[], filename: string) => {
    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.json`;
    link.click();
  };

  const toggleField = (fieldKey: string) => {
    setConfig(prev => ({
      ...prev,
      fields: prev.fields.includes(fieldKey)
        ? prev.fields.filter(f => f !== fieldKey)
        : [...prev.fields, fieldKey]
    }));
  };

  const selectAllFields = () => {
    if (!selectedOption) return;
    setConfig(prev => ({
      ...prev,
      fields: selectedOption.availableFields.map(f => f.key)
    }));
  };

  const clearAllFields = () => {
    setConfig(prev => ({ ...prev, fields: [] }));
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Advanced Export
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Advanced Data Export</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Data Source Selection */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Select Data Source</Label>
              <p className="text-sm text-muted-foreground">Choose what data you want to export</p>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {exportOptions.map(option => {
                const Icon = option.icon;
                const isSelected = selectedOption?.id === option.id;
                return (
                  <Card
                    key={option.id}
                    className={`p-4 cursor-pointer transition-colors ${
                      isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => {
                      setSelectedOption(option);
                      setConfig(prev => ({ ...prev, fields: [] }));
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className="w-5 h-5 mt-0.5 text-muted-foreground" />
                      <div className="flex-1">
                        <h3 className="font-medium">{option.title}</h3>
                        <p className="text-sm text-muted-foreground">{option.description}</p>
                        <div className="flex gap-1 mt-2">
                          {option.supportedFormats.map(format => (
                            <Badge key={format} variant="secondary" className="text-xs">
                              {format.toUpperCase()}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Export Configuration */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Export Configuration</Label>
              <p className="text-sm text-muted-foreground">Configure your export settings</p>
            </div>

            {selectedOption && (
              <div className="space-y-4">
                {/* Format Selection */}
                <div>
                  <Label>Export Format</Label>
                  <Select 
                    value={config.format} 
                    onValueChange={(value: any) => setConfig(prev => ({ ...prev, format: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedOption.supportedFormats.map(format => (
                        <SelectItem key={format} value={format}>
                          {format.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Range */}
                <div>
                  <Label>Date Range (Optional)</Label>
                  <div className="flex gap-2 mt-1">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm">
                          {config.dateRange.start ? format(config.dateRange.start, 'MMM dd, yyyy') : 'Start Date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent>
                        <CalendarComponent
                          mode="single"
                          selected={config.dateRange.start || undefined}
                          onSelect={(date) => setConfig(prev => ({
                            ...prev,
                            dateRange: { ...prev.dateRange, start: date || null }
                          }))}
                        />
                      </PopoverContent>
                    </Popover>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm">
                          {config.dateRange.end ? format(config.dateRange.end, 'MMM dd, yyyy') : 'End Date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent>
                        <CalendarComponent
                          mode="single"
                          selected={config.dateRange.end || undefined}
                          onSelect={(date) => setConfig(prev => ({
                            ...prev,
                            dateRange: { ...prev.dateRange, end: date || null }
                          }))}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Field Selection */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Fields to Include</Label>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={selectAllFields}>
                        Select All
                      </Button>
                      <Button variant="ghost" size="sm" onClick={clearAllFields}>
                        Clear All
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2 mt-2 max-h-40 overflow-y-auto">
                    {selectedOption.availableFields.map(field => (
                      <div key={field.key} className="flex items-center space-x-2">
                        <Checkbox
                          id={field.key}
                          checked={config.fields.includes(field.key)}
                          onCheckedChange={() => toggleField(field.key)}
                        />
                        <Label
                          htmlFor={field.key}
                          className="text-sm font-normal flex-1 cursor-pointer"
                        >
                          {field.label}
                          <Badge variant="outline" className="ml-2 text-xs">
                            {field.type}
                          </Badge>
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Export Button */}
                <Button 
                  onClick={handleExport} 
                  disabled={isExporting || config.fields.length === 0}
                  className="w-full"
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent mr-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Export {selectedOption.title}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};